import PropTypes from "prop-types"
import { useCallback } from "react"
import { useSelector } from "react-redux"

import { openModal } from "components/modal"
import { IS_NA, IS_CA, IS_JEWLR } from "helpers/application"
import { showHolidayShippingInfoPDP } from "helpers/holiday"
import { cn } from "styles/utils"

import { ActionBtn, Text, ShipDate } from "./components"

const DeliveryMessage = ({ delivery, holidayStyle, showPeriod = false }) => {
  if (!delivery) return null

  const isRange = !!delivery.date_range

  return (
    <Text pdpPricingHierarchyExp>
      {isRange ? (
        <>
          {"Get it "}
          <ShipDate holidayStyle={holidayStyle} pdpPricingHierarchyExp>
            {delivery.date_range[0]}
          </ShipDate>
          {" to "}
          <ShipDate holidayStyle={holidayStyle} pdpPricingHierarchyExp>
            {delivery.date_range[1]}
          </ShipDate>
        </>
      ) : (
        <>
          {"Get it by "}
          <ShipDate holidayStyle={holidayStyle} pdpPricingHierarchyExp>
            {delivery.date}
          </ShipDate>
        </>
      )}
      {" with "}
      <ShipDate holidayStyle={holidayStyle} pdpPricingHierarchyExp>
        {delivery.type}
      </ShipDate>
      {showPeriod && "."}
    </Text>
  )
}

DeliveryMessage.propTypes = {
  delivery: PropTypes.shape({
    date: PropTypes.string,
    date_range: PropTypes.arrayOf(PropTypes.string),
    type: PropTypes.string,
  }),
  holidayStyle: PropTypes.bool,
  showPeriod: PropTypes.bool,
}

const ShipText = () => {
  const hasDiamondSelector = useSelector(
    (state) => state.wizard.hasDiamondSelector
  )
  const ship = useSelector((state) => state.wizard.shipping.main)
  const holidayDelivery = ship?.available_holiday_delivery

  const pdpPricingHierarchyExp = useSelector(
    (state) => state.experiment.pdpPricingHierarchy.variant_id
  )

  const is_ca = IS_CA()
  const is_na = IS_NA()
  const holidayStyle = showHolidayShippingInfoPDP() && holidayDelivery
  const cutoffStyle = showHolidayShippingInfoPDP() && !holidayDelivery

  const showShippingInfo = useCallback(() => openModal("#shipping-info"), [])

  if (ship.view_type === "OUT_OF_STOCK") {
    return (
      <Text>
        We&apos;re sorry, this item is currently unavailable. Please check back
        soon!
      </Text>
    )
  }

  if (pdpPricingHierarchyExp === 1) {
    if (ship.delivery_dates?.length > 0) {
      const isMultiple = ship.delivery_dates.length > 1

      return (
        <>
          {ship.delivery_dates.map((delivery, index) => (
            <DeliveryMessage
              delivery={delivery}
              holidayStyle={holidayStyle}
              key={index}
              showPeriod={!isMultiple}
            />
          ))}
          {is_ca && !hasDiamondSelector && (
            <>
              {isMultiple ? (
                <Text>
                  Your exact delivery dates will be calculated at checkout
                </Text>
              ) : (
                <div>
                  <ActionBtn onClick={showShippingInfo}>
                    Change your postal code
                  </ActionBtn>
                </div>
              )}
            </>
          )}
        </>
      )
    }

    if (ship.delivery_date_range) {
      return (
        <>
          <DeliveryMessage
            delivery={{
              date_range: ship.delivery_date_range,
              type: `${IS_JEWLR() ? "Free" : "Standard"} Shipping`,
            }}
            holidayStyle={holidayStyle}
            showPeriod
          />
          <Text>Your exact delivery dates will be calculated at checkout</Text>
        </>
      )
    }

    // Default no ship methods returned
    return (
      <Text>
        Get it by <ShipDate holidayStyle={holidayStyle}>{ship.esd}</ShipDate>.
      </Text>
    )
  }

  if (ship.delivery_dates?.length > 0) {
    if (ship.delivery_dates.length === 2) {
      const free_is_range = ship.delivery_dates[1].date_range
      const express_is_range = ship.delivery_dates[0].date_range
      return (
        <div className={cn(holidayStyle && "tablet:mb-0 -mb-8")}>
          <Text
            cutoffStyle={cutoffStyle}
            holidayStyle={holidayStyle}
            second={!is_na}
          >
            {express_is_range && (
              <>
                Get it{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={!is_na}
                >
                  {ship.delivery_dates[0].date_range[0]}
                </ShipDate>{" "}
                to{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={!is_na}
                >
                  {ship.delivery_dates[0].date_range[1]}
                </ShipDate>{" "}
                with{" "}
              </>
            )}
            {!express_is_range && (
              <>
                Get it by{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={!is_na}
                >
                  {ship.delivery_dates[0].date}
                </ShipDate>{" "}
                with{" "}
              </>
            )}
            <ShipDate
              cutoffStyle={cutoffStyle}
              holidayStyle={holidayStyle}
              second={!is_na}
            >
              {ship.delivery_dates[0].type}
            </ShipDate>
          </Text>
          {/* <Text holidayStyle={(is_au || is_uk) && holidayDelivery}> */}
          <Text
            cutoffStyle={cutoffStyle}
            holidayStyle={holidayStyle}
            second={is_na}
          >
            {free_is_range && (
              <>
                Get it{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={is_na}
                >
                  {ship.delivery_dates[1].date_range[0]}
                </ShipDate>{" "}
                to{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={is_na}
                >
                  {ship.delivery_dates[1].date_range[1]}
                </ShipDate>{" "}
                with{" "}
              </>
            )}
            {!free_is_range && (
              <>
                Get it by{" "}
                <ShipDate
                  cutoffStyle={cutoffStyle}
                  holidayStyle={holidayStyle}
                  second={is_na}
                >
                  {ship.delivery_dates[1].date}
                </ShipDate>{" "}
                with{" "}
              </>
            )}
            <ShipDate
              cutoffStyle={cutoffStyle}
              holidayStyle={holidayStyle}
              second={is_na}
            >
              {ship.delivery_dates[1].type}
            </ShipDate>
          </Text>
          {is_ca && (
            <Text>
              Your exact delivery dates will be calculated at checkout
            </Text>
            // <ActionBtn onClick={() => showShippingInfo()}>
            //   Change your postal code
            // </ActionBtn>
          )}
        </div>
      )
    } else {
      // Only one shipping method
      return (
        <>
          <Text cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
            Get it by{" "}
            <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
              {ship.delivery_dates[0].date}
            </ShipDate>{" "}
            with{" "}
            <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
              {ship.delivery_dates[0].type}
            </ShipDate>
            .
          </Text>
          {is_ca && !hasDiamondSelector && (
            <div>
              <ActionBtn onClick={() => showShippingInfo()}>
                Change your postal code
              </ActionBtn>
            </div>
          )}
        </>
      )
    }
  } else if (ship.delivery_date_range) {
    // CA no postal code
    return (
      <div className={cn(holidayStyle && "mb-8")}>
        <Text cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
          Get it{" "}
          <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
            {ship.delivery_date_range[0]}
          </ShipDate>{" "}
          to{" "}
          <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
            {ship.delivery_date_range[1]}
          </ShipDate>{" "}
          with{" "}
          <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
            {IS_JEWLR() ? "FREE" : "Standard"} Shipping
          </ShipDate>
          .
        </Text>
        {/* <Text cutoffStyle={cutoffStyle} holidayStyle={holidayStyle} second>
          To find out the exact delivery date,{" "}
          <ActionBtn
            className="mx-0"
            data-cy="change-postal-button"
            onClick={() => showShippingInfo()}
          >
            check your postal code
          </ActionBtn>
          .
        </Text> */}

        <Text>Your exact delivery dates will be calculated at checkout</Text>
      </div>
    )
  } else if (ship.view_type === "OUT_OF_STOCK") {
    return (
      <Text>
        We&apos;re sorry, this item is currently unavailable. Please check back
        soon!
      </Text>
    )
  } else {
    // Default no ship methods returned

    return (
      <Text cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
        Get it by{" "}
        <ShipDate cutoffStyle={cutoffStyle} holidayStyle={holidayStyle}>
          {ship.esd}
        </ShipDate>
        .
      </Text>
    )
  }
}

export default ShipText
